import { createContext, useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { axiosSW } from "../api/axiosDefaults";
import useLocalStorage from "../hooks/useLocalStorage";
import DataContext from "./DataContext";
import AuthContext from "./AuthContext";
import useSessionStorage from "../hooks/useSessionStorage";
import useAuthReady from "../hooks/useAuthReady";
import { fetchTickets } from "./fetchTicketsPosHandler";

const STATUS_TYPE = {
    0: "error",
    1: "searched ticket not found",
    2: "searched ticket rerserved",
    3: "searched ticket paid",
    4: "searched ticket found",
    5: "settlement reserved",
    6: "settlement paid",
    7: "searched ticket in settlement",
    8: "unhandled",
    9: "inprocess",
};

const POSDataContext = createContext({});

export const POSDataProvider = ({ children }) => {
    const [isLoadingPos, setIsLoadingPos] = useState(false);
    const [isProcessingPos, setIsProcessingPos] = useState(false);
    const [isProcessingChargesPos, setIsProcessingChargesPos] = useState(false);
    const [resultPos, setResultPos] = useState(false);
    const [summaryPos, setSummaryPos] = useState(null);
    const [currentTicketPos, setCurrentTicketPos] = useState({});
    const [currentCategoryPos, setCurrentCategoryPos] = useState("");
    const [shoppingCartItems, setShoppingCartItems] = useState([]);
    const [shoppingCartTotal, setShoppingCartTotal] = useState(0);
    const [isAddHoverPos, setIsAddHoverPos] = useState(false);
    const [isValidSearchPos, setIsValidSearchPos] = useSessionStorage(
        "isValidSearchPos",
        false,
    );
    const [responseDataPos, setResponseDataPos] = useState({});
    const [filterExpression, setFilterExpression] = useSessionStorage(
        "filterExpression",
        null,
    );
    const [resultMount, setRresultMount] = useSessionStorage(
        "resultMount",
        false,
    );

    const [selectedTicketsPos, setSelectedTicketsPos] = useState([]);
    const [selectedInstalmentsPos, setSelectedInstalmentsPos] = useState([]);
    const [isInstallemtHoverPos, setIsInstallemtHoverPos] = useState("");
    const [ticketTypePos, setTicketTypePos] = useState(null);

    const [payments, setPayments] = useLocalStorage("payments", []);
    const [count, setCount] = useSessionStorage("count", 0);
    // const [countUpdated, setCountupdated] = useSessionStorage(
    //     "countUpdated",
    //     false
    // );
    const [isCountValid, setIsCountValid] = useState(false);
    const navigate = useNavigate();
    const { homePath, connection, setIsServerDown, JWTToken } =
        useContext(DataContext);
    const { user, logout } = useContext(AuthContext);

    const location = useLocation();

    const fetchTicketsWithContext = (payLoad, upDate) => {
        fetchTickets(payLoad, upDate, {
            setIsLoadingPos,
            setResponseDataPos,
            setResultPos,
            setCurrentTicketPos,
            setCurrentCategoryPos,
            navigate,
            handleErrorPos,
            homePath,
            setIsProcessingPos,
            setRresultMount,
            setIsValidSearchPos,
            location,
            setIsProcessingChargesPos,
        });
    };

    // Force reload all tabs if user change or logout
    useEffect(() => {
        const handler = (event) => {
            if (event.key === "user" && event.oldValue !== event.newValue) {
                window.location.href = homePath;
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, [navigate]);

    // Fetch the shopping cart when the user is logged in and the page is POS
    // the function will be called only if the iscount (is there a valid count) false and there is no count (items in the cart)
    // to avoid multiple calls to the server
    useEffect(() => {
        if (
            user &&
            JWTToken &&
            connection?.uniqueIdentifier &&
            location.pathname.includes("/pos") &&
            !location.pathname.includes("/customization") &&
            !location.pathname.includes("/cash-operations") &&
            !location.pathname.includes("/summary") &&
            !isCountValid &&
            !count
        ) {
            fetchShoppingCart();
        }
    }, [user, connection?.uniqueIdentifier, location?.pathname, JWTToken]);

    // ------------------------------------------ update shoppingcart on countUpdated
    // useEffect(() => {
    //     if (
    //         user &&
    //         JWTToken &&
    //         connection?.uniqueId &&
    //         location.pathname.includes("/pos") &&
    //         !location.pathname.includes("/customization") &&
    //         isCountValid
    //     ) {
    //         if (countUpdated) {
    //             fetchShoppingCart();
    //             setCountupdated(false);
    //         }
    //     }
    // }, [
    //     user,
    //     connection?.uniqueId,
    //     location?.pathname,
    //     JWTToken,
    //     countUpdated,
    // ]);
    // ---------------------------------------------

    /**
     * Add the value to the count (items in the cart)
     * @param {Integer} value = the value that will be added to the count
     */
    const incCount = (value) => {
        setCount((prev) => prev + value);
    };

    /**
     * Submit error log to Schweers endpoint
     * Currently not used
     * @param {Object} log - object that contains the error information
     */
    const submitError = async (log) => {
        try {
            const response = await axiosSW.post(
                "Logging/PolitessPaymentLogging",
                log,
            );
        } catch (error) {
            console.log("There was an error saving the data!", error);
        }
    };

    /**
     * Forwarding and logging the error that occurs
     * @param {*} error - object that contains the error information
     */
    const handleErrorPos = (error) => {
        const errorLog = {
            userID: connection?.customerID ? connection?.customerID : -1,
            filterExpression: filterExpression ? filterExpression : "",
            status: error?.response?.status ? error?.response?.status : -1,
            message: error?.message ? error?.message : "",
            code: error?.code ? error?.code : "",
            timeStamp: moment().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
            config: {
                method: error?.config?.method ? error?.config?.method : "",
                url: error?.config?.url ? error?.config?.url : "",
                data: error?.config?.data ? error?.config?.data : "",
            },
        };
        console.log("error:", error);
        console.log(errorLog);
        // submitError(errorLog);
        const errorObj = {
            errorLog: errorLog,
            error: error,
            errorMessage: error?.message,
        };
        setIsLoadingPos(false);
        setResultPos(errorObj);
        setIsServerDown(true);

        navigate(`${homePath}/result`);
    };

    /**
     * Logout the current user and redirect to login page
     */
    const userLogout = async () => {
        await logout();
        navigate(homePath);
    };

    /**
     * Fetch tickets from the server
     * The fetchTickets functions is also used for updating the tickets when adding to the shopping cart
     * it will use setIsLoadingPos() if the search is valid and if not will use setIsProcessingPos()
     * this way we can achieve showing the spinner inside the result panel without rerendering the panel it self while fetching the data
     * @param {Object} payLoad - object that contains the search parameters
     * @param {Boolean} upDate - boolean that indicates if the function is used for updating the tickets
     */

    const [currentfilter, setcurrentfilter] = useState("");
    useEffect(() => {
        setcurrentfilter(filterExpression?.filterExpression);
    }, [filterExpression?.filterExpression]);

    // -----------------------------------------------------------------------------------  Moved to seperate js file for unit testing
    // const fetchTickets = async (payLoad, upDate) => {
    //     try {
    //         upDate ? setIsProcessingPos(true) : setIsLoadingPos(true);
    //         // setIsLoadingPos(true)
    //         setResponseDataPos(null);
    //         setResultPos(null);

    //         const { data } = await axiosSW.post(
    //             "/Ticket/pos/GetTicketData",
    //             payLoad
    //         );

    //         setResponseDataPos(data);

    //         switch (data?.outputStatus) {
    //             case 1:
    //                 setResultPos(data);
    //                 navigate(`${homePath}/result`);
    //                 upDate ? setIsProcessingPos(false) : setIsLoadingPos(false);
    //                 return;
    //             case 2:
    //             case 3:
    //             case 5:
    //             case 6:
    //             case 7:
    //             case 8:
    //             case 9:
    //                 setCurrentTicketPos(data?.relatedTickets?.[0]);
    //                 setCurrentCategoryPos(
    //                     data?.relatedTickets?.[0]?.photos[0]?.category
    //                 );

    //                 // The function is used for both fetching and updating tickets
    //                 // if update set to true  it will not navigate to result page again and not set these states :
    //                 !upDate && navigate(`${homePath}/tickets/result`);
    //                 !upDate && setIsValidSearchPos(true);

    //                 // first result page mount, it is set to true and on the first mount of the result page it will be set to fault
    //                 //and the consequent refresh will update fetch the teckets without redirecting to result page one more time
    //                 !upDate && setRresultMount(true);
    //                 upDate ? setIsProcessingPos(false) : setIsLoadingPos(false);
    //                 return;
    //             case 4:
    //                 setCurrentTicketPos(data?.ticket);
    //                 setCurrentCategoryPos(data?.ticket?.photos[0]?.category);

    //                 // The function is used for both fetching and updating tickets
    //                 // if update set to true  it will not navigate to result page again and not set these states :
    //                 !upDate && navigate(`${homePath}/tickets/result`);
    //                 !upDate && setIsValidSearchPos(true);

    //                 // first result page mount, it is set to true and on the first mount of the result page it will be set to fault
    //                 //and the consequent refresh will update fetch the teckets without redirecting to result page one more time
    //                 !upDate && setRresultMount(true);
    //                 upDate ? setIsProcessingPos(false) : setIsLoadingPos(false);

    //                 setCurrentTicketPos(data?.ticket);
    //                 setCurrentCategoryPos(data?.ticket?.photos[0]?.category);

    //                 // The function is used for both fetching and updating tickets
    //                 // if update set to true  it will not navigate to result page again and not set these states :
    //                 !upDate && navigate(`${homePath}/tickets/result`);
    //                 !upDate && setIsValidSearchPos(true);

    //                 // first result page mount, it is set to true and on the first mount of the result page it will be set to fault
    //                 //and the consequent refresh will update fetch the teckets without redirecting to result page one more time
    //                 !upDate && setRresultMount(true);
    //                 upDate ? setIsProcessingPos(false) : setIsLoadingPos(false);

    //                 return;

    //             default:
    //                 upDate ? setIsProcessingPos(false) : setIsLoadingPos(false);
    //                 handleErrorPos({
    //                     error: data,
    //                     errorMessage: "Bad response",
    //                 });
    //         }

    //     } catch (error) {
    //         upDate ? setIsProcessingPos(false) : setIsLoadingPos(false);
    //         // setIsLoadingPos(false);
    //         handleErrorPos(error);
    //     }
    // };

    //------------------------------------------------------------------------------------------

    /**
     * Fetch the shopping cart from the server
     */
    const fetchShoppingCart = async () => {
        const payLoad = {
            uniqueIdentifier: connection?.uniqueIdentifier,
            cartID: user?.cartID,
        };

        setIsLoadingPos(true);
        try {
            const { data } = await axiosSW.post(
                "ShoppingCart/pos/GetShoppingCart",
                payLoad,
            );

            if (data?.statusOk) {
                setShoppingCartItems(data?.items);
                setShoppingCartTotal(data?.totalAmount);
                setCount(data?.totalCartItem);
                setIsCountValid(true);
                setIsLoadingPos(false);
            }
        } catch (error) {
            if (error?.request?.status === 404) {
                setIsLoadingPos(false);
                userLogout();
            } else {
                setIsLoadingPos(false);
                handleErrorPos(error);
            }
        }
    };

    /**
     * Fetch the summary for the current session
     */
    const fetchSummary = async () => {
        const payLoad = {
            uniqueIdentifier: connection?.uniqueIdentifier,
            cashierSessionID: user?.cashierSessionID,
        };

        setIsLoadingPos(true);
        try {
            const response = await axiosSW.post(
                "Statistic/pos/GetSalesReport",
                payLoad,
            );

            if (response?.data && response?.data?.statusOk) {
                setSummaryPos(response?.data);

                setIsLoadingPos(false);
            } else if (
                response?.data &&
                !response?.data?.statusOk &&
                response?.status === 200
            ) {
                setIsLoadingPos(false);
                setSummaryPos(null);
            }
        } catch (error) {
            setIsLoadingPos(false);

            handleErrorPos(error);
        }
    };

    return (
        <POSDataContext.Provider
            value={{
                isLoadingPos, // {Boolean} loading state
                setIsLoadingPos,

                resultPos, // {Object} object that contains the result of the operatrion
                setResultPos,

                currentTicketPos, // {Object} object that contains the current ticket
                setCurrentTicketPos,

                currentCategoryPos, // {String} contains the current category from the ticket photos (set to the first category by default)
                setCurrentCategoryPos,

                isValidSearchPos, // {Boolean} indicates if the search is valid
                setIsValidSearchPos,

                responseDataPos, // {Object} object that contains the response data from the server
                setResponseDataPos,

                filterExpression, // {String} the filter expression used for the querry
                setFilterExpression,

                fetchTickets: fetchTicketsWithContext, // {Function} fetch the tickets from the server

                isProcessingPos, // {Boolean} indicates if the process is in progress
                setIsProcessingPos,

                isProcessingChargesPos,
                setIsProcessingChargesPos,

                fetchShoppingCart, // {Function} fetch the shopping cart from the server

                shoppingCartItems, // {Array} shopping cart items

                incCount, // {Function} increment the count of the items in the cart

                count, // {Integer} the count of the items in the cart
                setCount,

                payments, // {Array} array that contains the payments added to the cart
                setPayments,

                resultMount, // {Boolean} indicates if the page is mounted for the first time
                // starts as true and on the first mount of the serach result page it will be set to false
                // and then, the consequent refresh will update fetch the teckets
                // without redirecting to search result
                setRresultMount,

                handleErrorPos, // {Function} handle the error that occurs

                summaryPos, // {Object} object that contains the summary of the current session
                setSummaryPos,

                fetchSummary, // {Function} fetch the summary of the current session

                shoppingCartTotal, // {Float} the total amount (money) of the items in the cart

                userLogout,

                selectedTicketsPos,
                setSelectedTicketsPos,
                selectedInstalmentsPos,
                setSelectedInstalmentsPos,

                isInstallemtHoverPos,
                setIsInstallemtHoverPos,

                ticketTypePos,
                setTicketTypePos,

                isAddHoverPos,
                setIsAddHoverPos,
            }}
        >
            {children}
        </POSDataContext.Provider>
    );
};

export default POSDataContext;
